import { useState, useMemo } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { useMacroStore } from '@/store/macroStore';
import { computeZScore, type ZScorePoint } from './macroMath';

interface ZScorePanelProps {
  seriesId: string;
  color: string;
  highlightedLag?: number | null;
  /** Pearson ρ with stock price — when negative, BUY/SELL labels are inverted. */
  pearsonR?: number | null;
}

interface ChartRow {
  date: string;
  dateLabel: string;
  zScore: number;
  laggedZScore?: number | null;
  signal: 'buy' | 'sell' | 'neutral';
  /** Signal adjusted for correlation direction (flipped when ρ < 0). */
  effectiveSignal: 'buy' | 'sell' | 'neutral';
}

/** Custom dot for signal markers. */
function SignalDot(props: {
  cx?: number;
  cy?: number;
  payload?: ChartRow;
  color: string;
}) {
  const { cx = 0, cy = 0, payload } = props;
  const signal = payload?.effectiveSignal;
  if (signal === 'buy') {
    return (
      <polygon
        points={`${cx},${cy - 5} ${cx - 4},${cy + 3} ${cx + 4},${cy + 3}`}
        fill="#22c55e"
      />
    );
  }
  if (signal === 'sell') {
    return (
      <polygon
        points={`${cx},${cy + 5} ${cx - 4},${cy - 3} ${cx + 4},${cy - 3}`}
        fill="#ef4444"
      />
    );
  }
  return null;
}

/** Custom tooltip for Z-score chart. */
function ZScoreTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    payload: ChartRow;
    value?: number;
    dataKey?: string;
  }>;
}) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  const z = item.zScore;
  const abs = Math.abs(z);
  const dir = z >= 0 ? 'above' : 'below';

  return (
    <div className="bg-panel border border-border rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-muted mb-0.5">{item.dateLabel}</p>
      <p className="font-medium text-text">z = {z.toFixed(2)}</p>
      <p className="text-[11px] text-muted mt-0.5">
        {abs.toFixed(1)}σ {dir} rolling average
      </p>
      {item.effectiveSignal !== 'neutral' && (
        <p
          className="text-[11px] font-semibold mt-1"
          style={{ color: item.effectiveSignal === 'buy' ? '#22c55e' : '#ef4444' }}
        >
          Signal: {item.effectiveSignal.toUpperCase()}
        </p>
      )}
    </div>
  );
}

export function ZScorePanel({ seriesId, color, highlightedLag, pearsonR }: ZScorePanelProps) {
  const seriesData = useMacroStore((s) => s.seriesData[seriesId]);

  const [lookback, setLookback] = useState(12);
  const [buyThreshold, setBuyThreshold] = useState(1.5);
  const [sellThreshold, setSellThreshold] = useState(-1.5);
  const [excludeCrisis, setExcludeCrisis] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // When Pearson ρ is negative, high indicator is bearish → invert BUY/SELL display
  const flipSignal = (pearsonR ?? 0) < 0;

  // Compute z-score series
  const zPoints: ZScorePoint[] = useMemo(() => {
    if (!seriesData || seriesData.length < lookback + 3) return [];
    let points = seriesData;
    if (excludeCrisis) {
      points = points.filter((p) => p.date < '2022-03-01' || p.date > '2022-09-30');
    }
    return computeZScore(points, lookback, buyThreshold, sellThreshold);
  }, [seriesData, lookback, buyThreshold, sellThreshold, excludeCrisis]);

  // Build chart rows
  const chartData: ChartRow[] = useMemo(() => {
    if (zPoints.length === 0) return [];
    return zPoints.map((p, i) => {
      const d = new Date(p.date + 'T00:00:00');
      const dateLabel = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

      let laggedZScore: number | null = null;
      if (highlightedLag != null && highlightedLag !== 0) {
        const targetIdx = i - highlightedLag;
        if (targetIdx >= 0 && targetIdx < zPoints.length) {
          laggedZScore = zPoints[targetIdx].zScore;
        }
      }

      const effectiveSignal: 'buy' | 'sell' | 'neutral' =
        flipSignal && p.signal !== 'neutral'
          ? p.signal === 'buy' ? 'sell' : 'buy'
          : p.signal;
      return {
        date: p.date,
        dateLabel,
        zScore: p.zScore,
        laggedZScore,
        signal: p.signal,
        effectiveSignal,
      };
    });
  }, [zPoints, highlightedLag, flipSignal]);

  const latest = zPoints[zPoints.length - 1];
  const latestChart = chartData[chartData.length - 1];

  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [-3, 3];
    const vals = chartData.map((d) => d.zScore);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = 0.5;
    return [Math.min(-3, min - pad), Math.max(3, max + pad)] as [number, number];
  }, [chartData]);

  return (
    <div className="flex gap-3 h-full min-h-[320px]">
      {/* ── Left strip (140px) ─────────────────────────────────────── */}
      <div className="w-[140px] shrink-0 flex flex-col gap-2">
        <div>
          <p className="text-[10px] text-muted uppercase tracking-wider">Current Z-Score</p>
          <p
            className="text-xl font-bold"
            style={{
              color:
                latestChart?.effectiveSignal === 'buy'
                  ? '#22c55e'
                  : latestChart?.effectiveSignal === 'sell'
                    ? '#ef4444'
                    : 'var(--color-text)',
            }}
          >
            {latest ? latest.zScore.toFixed(2) : '—'}
          </p>
        </div>

        {latestChart && (
          <>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold w-fit"
            style={{
              background:
                latestChart.effectiveSignal === 'buy'
                  ? 'rgba(34,197,94,0.15)'
                  : latestChart.effectiveSignal === 'sell'
                    ? 'rgba(239,68,68,0.15)'
                    : 'rgba(148,163,184,0.15)',
              color:
                latestChart.effectiveSignal === 'buy'
                  ? '#22c55e'
                  : latestChart.effectiveSignal === 'sell'
                    ? '#ef4444'
                    : '#94a3b8',
            }}
          >
            {latestChart.effectiveSignal === 'buy' ? 'BUY' : latestChart.effectiveSignal === 'sell' ? 'SELL' : 'NEUTRAL'}
          </span>
          {flipSignal && latestChart.effectiveSignal !== 'neutral' && (
            <p className="text-[9px] text-muted mt-0.5">neg. correlation inverted</p>
          )}
        </>
        )}

        <div className="mt-auto">
          <button
            onClick={() => setShowConfig((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-muted hover:text-text transition-colors"
            title="Configure Z-Score"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.5 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.5 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.5a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 21 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Config
          </button>

          {showConfig && (
            <div className="mt-2 bg-panel-bg border border-border rounded-lg p-2.5 flex flex-col gap-2.5">
              <div>
                <label className="text-[10px] text-muted block mb-1">Lookback {lookback} mo</label>
                <input
                  type="range"
                  min={6}
                  max={60}
                  value={lookback}
                  onChange={(e) => setLookback(Number(e.target.value))}
                  className="w-full accent-accent"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-muted block mb-1">Buy ≥</label>
                  <input
                    type="number"
                    step={0.1}
                    value={buyThreshold}
                    onChange={(e) => setBuyThreshold(Number(e.target.value))}
                    className="w-full px-1.5 py-1 text-xs bg-bg border border-border rounded text-text"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-muted block mb-1">Sell ≤</label>
                  <input
                    type="number"
                    step={0.1}
                    value={sellThreshold}
                    onChange={(e) => setSellThreshold(Number(e.target.value))}
                    className="w-full px-1.5 py-1 text-xs bg-bg border border-border rounded text-text"
                  />
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeCrisis}
                  onChange={(e) => setExcludeCrisis(e.target.checked)}
                  className="accent-accent"
                />
                Exclude 2022 crisis
              </label>
            </div>
          )}
        </div>
      </div>

      {/* ── Center chart ───────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted">
            Insufficient data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 9, fill: 'var(--color-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={yDomain}
                tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
                width={30}
              />
              <Tooltip content={<ZScoreTooltip />} cursor={{ stroke: 'var(--color-border)' }} />

              {/* Reference lines */}
              <ReferenceLine y={0} stroke="var(--color-border)" />
              <ReferenceLine y={1} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={-1} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={2} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.3} />
              <ReferenceLine y={-2} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.3} />

              {/* Signal zones */}
              <ReferenceArea
                y1={buyThreshold}
                y2={yDomain[1]}
                fill="#22c55e"
                fillOpacity={0.06}
              />
              <ReferenceArea
                y1={yDomain[0]}
                y2={sellThreshold}
                fill="#ef4444"
                fillOpacity={0.06}
              />

              {/* Crisis shading */}
              <ReferenceArea
                x1="Mar 2022"
                x2="Sep 2022"
                fill="#ef4444"
                fillOpacity={0.08}
                label={{
                  value: '2022 crisis — correlations unreliable',
                  position: 'insideTopLeft',
                  fontSize: 9,
                  fill: '#ef4444',
                }}
              />

              {/* Main z-score line */}
              <Line
                type="monotone"
                dataKey="zScore"
                stroke={color}
                strokeWidth={1.5}
                dot={<SignalDot color={color} />}
                activeDot={{ r: 4, fill: color }}
              />

              {/* Lagged line (if CCF selected a lag) */}
              {highlightedLag != null && highlightedLag !== 0 && (
                <Line
                  type="monotone"
                  dataKey="laggedZScore"
                  stroke={color}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  dot={false}
                  name={`Lagged ${fmtLag(highlightedLag)} (from CCF)`}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function fmtLag(lag: number): string {
  return lag > 0 ? `+${lag}` : `${lag}`;
}
